import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, BarChart3, Building2, Calendar, Check, ClipboardList, Clock, Copy, DollarSign, History, Star, Trash2, UserPlus, Users, X } from 'lucide-react';
import {
  useTeamStats,
  useCreateInvite,
  useActiveInvites,
  useRevokeInvite,
  type MechanicStats,
} from '@/hooks/useTeamStats';
import {
  useScheduleToday,
  useMechanicSchedule,
  useReplaceSchedule,
  useCreateAbsence,
  type ScheduleTodayMechanic,
} from '@/hooks/useMechanicHR';
import { useAuth } from '@/hooks/useAuth';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useCan } from '@/hooks/useCan';
import { ROLE_LABELS, memberRoleLabel, type MemberRole } from '@mustauto/types/permissions';
import { PeopleTab } from '@/components/team/PeopleTab';
import { InviteDialog } from '@/components/team/InviteDialog';
import { TeamHistoryTab } from '@/components/team/TeamHistoryTab';
import { Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, useToast } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { formatReais } from '@/lib/utils';
import { formatApiError } from '@mustauto/utils';

type Tab = 'people' | 'performance' | 'schedules' | 'history';
type SortKey = 'performance' | 'revenue' | 'os_count';

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const DAY_NAMES_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

function getCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "em 6 dias", "amanhã", "hoje" — o gestor decide pelo prazo, não pela data. */
function formatExpiry(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'amanhã';
  return `em ${days} dias`;
}

/** "28/10" — o dia em que o convite vence, no fuso de quem lê. */
function formatExpiryDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Desempenho = faturamento contra a média da equipe. Sem `painel.faturamento`
 * a API não manda o faturamento, e a base vira as OS concluídas — `avgRevenue`
 * é a média dessa mesma base.
 */
function performanceBasis(m: MechanicStats): number {
  return m.revenue ?? m.completedMonth;
}

function getPerformanceScore(m: MechanicStats, avgRevenue: number): number {
  if (avgRevenue <= 0) return 100;
  return Math.round((performanceBasis(m) / avgRevenue) * 100);
}

function PerformanceBar({ score }: { score: number }) {
  const clamped = Math.min(score, 200);
  const width = Math.min(clamped, 100);
  const color =
    score >= 120
      ? 'bg-emerald-500'
      : score >= 80
        ? 'bg-blue-500'
        : 'bg-amber-500';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Desempenho</span>
        <span className="font-semibold">{score}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="text-[10px] text-muted-foreground">100% = média da equipe</p>
    </div>
  );
}

function MechanicCard({ m, avgRework, avgRevenue }: { m: MechanicStats; avgRework: number; avgRevenue: number }) {
  const navigate = useNavigate();
  const highRework = m.reworkRate > 0.15;
  const initial = m.name.charAt(0).toUpperCase();
  const perfScore = getPerformanceScore(m, avgRevenue);

  return (
    <Card
      className={`transition-shadow hover:shadow-md ${highRework ? 'border-warning/50' : ''}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {m.avatarUrl ? (
            <img
              src={m.avatarUrl}
              alt={m.name}
              className="h-12 w-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
              {initial}
            </div>
          )}

          <div className="flex-1 space-y-3">
            {/* Name + badge */}
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">{m.name}</span>
              {highRework && (
                <Badge variant="warning" className="gap-1">
                  <AlertTriangle size={12} />
                  Retrabalho {Math.round(m.reworkRate * 100)}%
                </Badge>
              )}
            </div>

            {/* Performance bar */}
            <PerformanceBar score={perfScore} />

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <ClipboardList size={14} />
                <span>
                  <strong className="text-foreground">{m.activeToday}</strong> em andamento
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <ClipboardList size={14} />
                <span>
                  <strong className="text-foreground">{m.completedMonth}</strong> no mês
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign size={14} />
                <span>
                  Faturamento: <strong className="text-foreground">{m.revenue == null ? '—' : formatReais(m.revenue)}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign size={14} />
                <span>
                  Ticket: <strong className="text-foreground">{m.avgTicket == null ? '—' : formatReais(m.avgTicket)}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock size={14} />
                <span>
                  <strong className="text-foreground">{m.avgExecutionHours.toFixed(1)}h</strong> média
                </span>
              </div>
              {m.avgRating !== null && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Star size={14} />
                  <span>
                    <strong className="text-foreground">{m.avgRating.toFixed(1)}</strong> avaliação
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons — spec 7.3 */}
            <div className="flex gap-2 border-t pt-3">
              <button
                onClick={() => navigate({ to: '/team/$mechanicId', params: { mechanicId: m.id } })}
                className="rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                Ver OS
              </button>
              <button
                onClick={() => navigate({ to: '/team/$mechanicId', params: { mechanicId: m.id }, search: { tab: 'history' } })}
                className="rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                Histórico
              </button>
              <button
                onClick={() => navigate({ to: '/team/$mechanicId', params: { mechanicId: m.id }, search: { tab: 'payments' } })}
                className="rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                Pagamento
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TeamPage() {
  const { user, isDemo } = useAuth();
  const { toast } = useToast();
  const [month] = useState(getCurrentMonth);
  const [activeTab, setActiveTab] = useState<Tab>('people');
  const team = useTeamMembers();
  const can = useCan();
  const stats = useTeamStats(month, can('equipe.ver_desempenho'));
  const invite = useCreateInvite();
  const viewerPerms = team.data?.viewer.permissions ?? [];
  const canInvite = viewerPerms.includes('equipe.convidar');
  const canSeeHistory = viewerPerms.includes('equipe.permissoes');
  const activeInvites = useActiveInvites(canInvite);
  const revokeInvite = useRevokeInvite();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState<{ code: string; role: MemberRole; renewed: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const mechanics = useMemo(() => stats.data?.mechanics ?? [], [stats.data]);

  const avgRework = useMemo(() => {
    if (!mechanics.length) return 0;
    const total = mechanics.reduce((s, m) => s + m.reworkRate, 0);
    return total / mechanics.length;
  }, [mechanics]);

  const avgRevenue = useMemo(() => {
    if (!mechanics.length) return 0;
    const total = mechanics.reduce((s, m) => s + performanceBasis(m), 0);
    return total / mechanics.length;
  }, [mechanics]);

  const reworkAlerts = useMemo(() => mechanics.filter((m) => m.reworkRate > 0.15), [mechanics]);

  if (!user?.workshopId) {
    return (
    <EmptyState
      icon={<Building2 size={28} />}
      title="Sua conta não está vinculada a uma oficina"
      description="Isso costuma ser sessão desatualizada: recarregue a página. Se continuar, o suporte resolve o vínculo."
      action={{ label: 'Recarregar', onClick: () => window.location.reload() }}
      secondaryAction={{ label: 'Falar com o suporte', href: '/support' }}
    />
    );
  }

  async function handleInvite(role: MemberRole) {
    try {
      const result = await invite.mutateAsync(role);
      setInviteOpen(false);
      setInviteCode({ code: result.code, role: result.role, renewed: result.renewed });
      setCopied(false);
      // Um convite ativo por papel, e gerar de novo devolve o mesmo código com
      // prazo novo: o que já foi mandado continua valendo, e o toast diz isso
      // para o gestor não achar que precisa mandar de novo.
      toast({
        type: 'success',
        title: result.renewed ? 'Convite renovado' : 'Convite gerado',
        description: result.renewed
          ? `É o mesmo código de antes. Agora vale até ${formatExpiryDate(result.expiresAt)}.` +
            // O que saiu foi um código EXTRA do papel (acúmulo de antes da regra).
            (result.revokedCount > 0 ? ' Outro código deste papel foi desativado.' : '')
          : result.revokedCount > 0
            ? 'O código anterior deste papel deixou de funcionar. Mande este novo.'
            : undefined,
      });
    } catch (err) {
      toast({ type: 'error', title: 'Falha ao gerar convite', description: formatApiError(err, 'Não foi possível gerar o convite. Tente novamente.') });
    }
  }

  async function handleRevoke(code: string) {
    try {
      await revokeInvite.mutateAsync(code);
      if (inviteCode?.code === code) setInviteCode(null);
      toast({ type: 'success', title: 'Convite revogado' });
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao revogar',
        description: formatApiError(err, 'Não foi possível revogar o convite. Tente novamente.'),
      });
    }
  }

  function handleCopy() {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'people', label: 'Pessoas', icon: <Users size={16} /> },
    ...(can('equipe.ver_desempenho') ? [{ key: 'performance' as const, label: 'Desempenho', icon: <BarChart3 size={16} /> }] : []),
    { key: 'schedules', label: 'Escalas', icon: <Calendar size={16} /> },
    ...(canSeeHistory ? [{ key: 'history' as const, label: 'Histórico', icon: <History size={16} /> }] : []),
  ];
  const memberCount = team.data?.members.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Equipe</h1>
          {memberCount !== undefined && (
            <p className="text-sm text-muted-foreground">
              {memberCount} pessoa{memberCount !== 1 ? 's' : ''} na oficina
            </p>
          )}
        </div>
        {!isDemo && canInvite && (
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
          >
            <UserPlus size={16} />
            Convidar
          </button>
        )}
      </div>

      <InviteDialog
        open={inviteOpen}
        viewerPermissions={viewerPerms}
        pending={invite.isPending}
        onCancel={() => setInviteOpen(false)}
        onConfirm={(role) => void handleInvite(role)}
      />

      {/* Invite code display */}
      {inviteCode && (
        <Card className="border-primary/30 bg-accent">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium text-foreground">
                Convite de {ROLE_LABELS[inviteCode.role]} {inviteCode.renewed ? 'renovado' : 'gerado'}:
              </p>
              <p className="mt-1 font-mono text-lg font-bold text-primary">
                {inviteCode.code}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                A pessoa usa este código no app para entrar na oficina como {ROLE_LABELS[inviteCode.role]}.
              </p>
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </CardContent>
        </Card>
      )}

      {/* Convites ativos — mostrados ANTES de o gestor pensar em gerar outro.
          É a parte que resolve o problema de origem: sem enxergar o que já
          existe, mecânico que não entra vira uma pilha de códigos vivos. */}
      {!isDemo && canInvite && activeInvites.data && activeInvites.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              Convites ativos ({activeInvites.data.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-4 pt-0">
            <p className="text-xs text-muted-foreground">
              Cada código vale 7 dias a partir da última vez que foi gerado e serve para
              várias pessoas. Para trocar o código, revogue o atual e gere outro — quem
              tiver o código consegue entrar na sua oficina.
            </p>
            {activeInvites.data.map((inv) => (
              <div
                key={inv.code}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm">{inv.code}</p>
                  <p className="text-xs text-muted-foreground">
                    {memberRoleLabel(inv.role)} · expira{' '}
                    {formatExpiry(inv.expiresAt)}
                  </p>
                </div>
                <button
                  onClick={() => handleRevoke(inv.code)}
                  disabled={revokeInvite.isPending}
                  aria-label={`Revogar convite ${inv.code}`}
                  className="flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  Revogar
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex shrink-0 items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'people' && <PeopleTab />}
      {activeTab === 'performance' && (
        // Erro antes do carregando — ver a nota em Reports.tsx: com o gate
        // único a aba ficava girando para sempre quando a busca falhava.
        stats.isError && !stats.data ? (
          <ErrorState title="Não foi possível carregar o desempenho da equipe" error={stats.error} onRetry={() => stats.refetch()} />
        ) : stats.isLoading || !stats.data ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <TeamTab
            mechanics={mechanics}
            avgRework={avgRework}
            avgRevenue={avgRevenue}
            reworkAlerts={reworkAlerts}
          />
        )
      )}
      {/* A escala sai da lista da equipe, não do desempenho: quem cuida do
          RH (`rh.gerir`) nem sempre vê o desempenho (`equipe.ver_desempenho`). */}
      {activeTab === 'schedules' && (
        team.isError && !team.data ? (
          <ErrorState title="Não foi possível carregar a equipe" error={team.error} onRetry={() => team.refetch()} />
        ) : team.isLoading || !team.data ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <SchedulesTab mechanics={team.data.members.map((m) => ({ id: m.userId, name: m.name }))} />
        )
      )}
      {activeTab === 'history' && canSeeHistory && <TeamHistoryTab />}
    </div>
  );
}

// ========================================
// Aba 1 — Equipe
// ========================================

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'performance', label: 'Desempenho' },
  { key: 'revenue', label: 'Faturamento' },
  { key: 'os_count', label: 'Qtd de OS' },
];

function TeamTab({
  mechanics,
  avgRework,
  avgRevenue,
  reworkAlerts,
}: {
  mechanics: MechanicStats[];
  avgRework: number;
  avgRevenue: number;
  reworkAlerts: MechanicStats[];
}) {
  const [sortBy, setSortBy] = useState<SortKey>('performance');

  const sorted = useMemo(() => {
    const arr = [...mechanics];
    switch (sortBy) {
      case 'performance':
        arr.sort((a, b) => getPerformanceScore(b, avgRevenue) - getPerformanceScore(a, avgRevenue));
        break;
      case 'revenue':
        arr.sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0));
        break;
      case 'os_count':
        arr.sort((a, b) => b.completedMonth - a.completedMonth);
        break;
    }
    return arr;
  }, [mechanics, sortBy, avgRevenue]);

  return (
    <div className="space-y-4">
      {/* Rework alerts */}
      {reworkAlerts.length > 0 && (
        <Card className="border-warning/50 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-warning" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                Alerta de retrabalho
              </p>
              {reworkAlerts.map((m) => {
                const multiplier = avgRework > 0 ? (m.reworkRate / avgRework).toFixed(1) : null;
                return (
                  <p key={m.id} className="mt-1 text-sm text-muted-foreground">
                    {m.reworkCount} OS de {m.name} tem{m.reworkCount !== 1 ? '' : ''}
                    {multiplier ? ` ${multiplier}x mais retrabalho que a média` : ' retrabalho acima da média'}
                  </p>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sort controls */}
      {mechanics.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Ordenar por:</span>
          {SORT_OPTIONS.filter((opt) => opt.key !== 'revenue' || mechanics.some((m) => m.revenue != null)).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortBy(opt.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                sortBy === opt.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Mechanic cards */}
      {sorted.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {sorted.map((m) => (
            <MechanicCard key={m.id} m={m} avgRework={avgRework} avgRevenue={avgRevenue} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Users size={28} />}
              title="Nenhum mecânico na equipe"
              description="Sua oficina ainda não tem mecânicos vinculados. Convide um mecânico usando o botão acima para começar."
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ========================================
// Aba 2 — Escalas
// ========================================

function SchedulesTab({ mechanics }: { mechanics: { id: string; name: string }[] }) {
  const canManageHR = useCan()('rh.gerir');
  const { data: todayData } = useScheduleToday();
  const [editingMechanic, setEditingMechanic] = useState<string | null>(null);
  const [absenceMechanic, setAbsenceMechanic] = useState<string | null>(null);

  // Map today data for quick lookup
  const todayMap = useMemo(() => {
    const map = new Map<string, ScheduleTodayMechanic>();
    todayData?.mechanics.forEach((m) => map.set(m.id, m));
    return map;
  }, [todayData]);

  if (mechanics.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum mecânico na equipe para exibir escalas.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      {todayData && (
        <Card>
          <CardContent className="flex items-center gap-6 p-4">
            <div>
              <p className="text-xs text-muted-foreground">Escalados Hoje</p>
              <p className="text-lg font-bold">{todayData.scheduledToday}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Ativos</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{todayData.activeToday}</p>
            </div>
            {todayData.absentToday > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Ausentes</p>
                <p className="text-lg font-bold text-red-600 dark:text-red-400">{todayData.absentToday}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Weekly grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Grade Semanal</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="py-2 pr-4 text-left font-medium text-muted-foreground">Mecânico</th>
                {DAY_NAMES.map((d) => (
                  <th key={d} className="min-w-[80px] py-2 text-center font-medium text-muted-foreground">
                    {d}
                  </th>
                ))}
                <th className="py-2 pl-4 text-center font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {mechanics.map((m) => (
                <ScheduleRow
                  key={m.id}
                  mechanic={m}
                  todayInfo={todayMap.get(m.id)}
                  onEdit={() => setEditingMechanic(m.id)}
                  onAbsence={() => setAbsenceMechanic(m.id)}
                  canManage={canManageHR}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit schedule modal */}
      {editingMechanic && (
        <EditScheduleModal
          mechanicId={editingMechanic}
          mechanicName={mechanics.find((m) => m.id === editingMechanic)?.name ?? ''}
          onClose={() => setEditingMechanic(null)}
        />
      )}

      {/* Absence modal */}
      {absenceMechanic && (
        <AbsenceModal
          mechanicId={absenceMechanic}
          mechanicName={mechanics.find((m) => m.id === absenceMechanic)?.name ?? ''}
          onClose={() => setAbsenceMechanic(null)}
        />
      )}
    </div>
  );
}

function ScheduleRow({
  mechanic,
  todayInfo,
  onEdit,
  onAbsence,
  canManage,
}: {
  mechanic: { id: string; name: string };
  todayInfo?: ScheduleTodayMechanic;
  onEdit: () => void;
  onAbsence: () => void;
  /** Escala e ausência são `rh.gerir` (Fase 3). */
  canManage: boolean;
}) {
  const { data } = useMechanicSchedule(mechanic.id);
  const entries = data?.entries ?? [];

  const scheduleByDay = useMemo(() => {
    const map = new Map<number, { startTime: string; endTime: string; active: boolean }>();
    entries.forEach((e) => map.set(e.dayOfWeek, e));
    return map;
  }, [entries]);

  const todayDow = new Date().getDay();

  return (
    <tr className="border-t">
      <td className="py-2 pr-4 font-medium">{mechanic.name}</td>
      {Array.from({ length: 7 }, (_, i) => {
        const entry = scheduleByDay.get(i);
        const isToday = i === todayDow;
        const isAbsent = isToday && todayInfo?.absent;

        if (!entry || !entry.active) {
          return (
            <td key={i} className="py-2 text-center">
              <div className="mx-auto h-8 w-16 rounded bg-muted" />
            </td>
          );
        }

        return (
          <td key={i} className="py-2 text-center">
            <div
              className={`mx-auto rounded px-1 py-1 text-xs font-medium ${
                isAbsent
                  ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  : isToday
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
              }`}
            >
              {entry.startTime.slice(0, 5)}–{entry.endTime.slice(0, 5)}
            </div>
          </td>
        );
      })}
      <td className="py-2 pl-4">
        {canManage && <div className="flex items-center justify-center gap-1">
          <button
            onClick={onEdit}
            className="rounded-md border px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Editar
          </button>
          <button
            onClick={onAbsence}
            className="rounded-md border px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 transition-colors"
          >
            Ausência
          </button>
        </div>}
      </td>
    </tr>
  );
}

function EditScheduleModal({
  mechanicId,
  mechanicName,
  onClose,
}: {
  mechanicId: string;
  mechanicName: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data, isLoading } = useMechanicSchedule(mechanicId);
  const replaceSchedule = useReplaceSchedule(mechanicId);

  const [entries, setEntries] = useState<
    Array<{ dayOfWeek: number; startTime: string; endTime: string; active: boolean }>
  >([]);
  const [initialized, setInitialized] = useState(false);

  // Init from data
  if (data && !initialized) {
    const existing = data.entries;
    const full = Array.from({ length: 7 }, (_, i) => {
      const found = existing.find((e) => e.dayOfWeek === i);
      return found
        ? { dayOfWeek: i, startTime: found.startTime.slice(0, 5), endTime: found.endTime.slice(0, 5), active: found.active }
        : { dayOfWeek: i, startTime: '08:00', endTime: '18:00', active: false };
    });
    setEntries(full);
    setInitialized(true);
  }

  const toggleDay = (day: number) => {
    setEntries((prev) =>
      prev.map((e) => (e.dayOfWeek === day ? { ...e, active: !e.active } : e)),
    );
  };

  const updateTime = (day: number, field: 'startTime' | 'endTime', value: string) => {
    setEntries((prev) =>
      prev.map((e) => (e.dayOfWeek === day ? { ...e, [field]: value } : e)),
    );
  };

  const handleSave = useCallback(async () => {
    const active = entries.filter((e) => e.active);
    try {
      await replaceSchedule.mutateAsync(active);
      toast({ type: 'success', title: 'Escala salva' });
      onClose();
    } catch (err) {
      toast({ type: 'error', title: 'Falha ao salvar escala', description: formatApiError(err, 'Não foi possível salvar a escala. Tente novamente.') });
    }
  }, [entries, replaceSchedule, onClose, toast]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="mx-4 w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Escala — {mechanicName}</CardTitle>
          <button
                onClick={onClose}
                aria-label="Fechar"
                className="text-muted-foreground hover:text-foreground"
              >
            <X size={18} />
          </button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <div key={e.dayOfWeek} className="flex items-center gap-3">
                  <label className="flex w-20 items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={e.active}
                      onChange={() => toggleDay(e.dayOfWeek)}
                      className="rounded border-input"
                    />
                    <span className="text-sm font-medium">{DAY_NAMES_FULL[e.dayOfWeek]}</span>
                  </label>
                  {e.active && (
                    <>
                      <input
                        type="time"
                        value={e.startTime}
                        onChange={(ev) => updateTime(e.dayOfWeek, 'startTime', ev.target.value)}
                        className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                      />
                      <span className="text-xs text-muted-foreground">até</span>
                      <input
                        type="time"
                        value={e.endTime}
                        onChange={(ev) => updateTime(e.dayOfWeek, 'endTime', ev.target.value)}
                        className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                      />
                    </>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSave}
                  disabled={replaceSchedule.isPending}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {replaceSchedule.isPending ? 'Salvando...' : 'Salvar Escala'}
                </button>
                <button
                  onClick={onClose}
                  className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AbsenceModal({
  mechanicId,
  mechanicName,
  onClose,
}: {
  mechanicId: string;
  mechanicName: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const createAbsence = useCreateAbsence(mechanicId);
  const [date, setDate] = useState('');
  const [type, setType] = useState<'vacation' | 'sick' | 'personal'>('sick');
  const [notes, setNotes] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        await createAbsence.mutateAsync({
          date,
          type,
          notes: notes || undefined,
        });
        toast({ type: 'success', title: 'Ausência registrada' });
        onClose();
      } catch (err) {
        toast({ type: 'error', title: 'Falha ao registrar ausência', description: formatApiError(err, 'Não foi possível registrar a ausência. Tente novamente.') });
      }
    },
    [date, type, notes, createAbsence, onClose, toast],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="mx-4 w-full max-w-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Registrar Ausência — {mechanicName}</CardTitle>
          <button
                onClick={onClose}
                aria-label="Fechar"
                className="text-muted-foreground hover:text-foreground"
              >
            <X size={18} />
          </button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Data *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Tipo *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="sick">Atestado / Doença</option>
                <option value="vacation">Férias</option>
                <option value="personal">Pessoal</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Observação</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                placeholder="Opcional"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={createAbsence.isPending || !date}
                className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {createAbsence.isPending ? 'Registrando...' : 'Registrar Ausência'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
